import { memo } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Box, Stack, Chip, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { withTransientProps } from "core/utils/transientProps";

const MIN_TOOLTIP_LENGTH = 29;

const SelectedFilters = memo(function SelectedFilteringOption(props) {
  const {
    selectedFilterValues,
    onSetFilters,
    enableClear,
    clearAllFilters,
    onDeleteStore,
    onClearStore
  } = props;

  const onDeleteFilter = (value, title) => {
    if (onDeleteStore) {
      onDeleteStore(value, title);
    } else {
      const filteredValues = selectedFilterValues[title].filter(el => {
        if (el?.type_name) {
          return el.type_name !== value.type_name;
        }
        return el.name !== value.name;
      });
      onSetFilters(filteredValues, title);
    }
  };

  const onClearAllFilters = () => {
    if (onClearStore) {
      onClearStore();
    } else {
      clearAllFilters();
    }
  };

  const getFormattedTitle = value => {
    return value.name &&
      ["__gte", "__lte"].some(el => value?.type_name?.includes(el))
      ? ` ${value?.type_name.includes("__gte") ? "from" : "to"}`
      : "";
  };

  const getFormattedFilterValue = value => {
    const date = new Date(value?.name * 1000);
    const isDate = value?.type === "date";
    return isDate ? date.toLocaleDateString() : value.name;
  };

  return (
    <Container $denseMode>
      <ChipArray component="ul">
        {Object.entries(selectedFilterValues).map(([title, values]) =>
          values.map((value, index) => (
            <li key={`${title}: ${value.name}_${index}`}>
              {/* If `Title: Value` length is too long, show tooltip */}
              <Tooltip
                title={
                  `${title}${getFormattedTitle(
                    value
                  )}: ${getFormattedFilterValue(value)}`.length >
                  MIN_TOOLTIP_LENGTH
                    ? getFormattedFilterValue(value)
                    : ""
                }
              >
                <FilterChip
                  label={
                    <ChipWrap>{`${title}${getFormattedTitle(
                      value
                    )}: ${getFormattedFilterValue(value)}`}</ChipWrap>
                  }
                  onDelete={() =>
                    onDeleteFilter(
                      values.find(filter =>
                        filter.type_name
                          ? filter.type_name === value.type_name
                          : filter.name === value.name
                      ),
                      title
                    )
                  }
                />
              </Tooltip>
            </li>
          ))
        )}
      </ChipArray>

      {enableClear ? (
        <Stack alignItems="center" direction="row">
          <IconButton onClick={onClearAllFilters} size="large">
            <CloseIcon />
          </IconButton>
        </Stack>
      ) : null}
    </Container>
  );
});

const ChipArray = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  list-style: none;
  padding: 0 5px;
  margin: 0;
  border-radius: 20px;
  min-height: 42px;
  flex: 1 1 100%;
  justify-content: left;
  flex-wrap: wrap;
  overflow: auto;
`;

const Container = styled(Box, withTransientProps)`
  display: flex;
  flex-direction: row;
  overflow: auto;
  max-height: ${({ $denseMode }) => ($denseMode ? "84px" : "unset")};
  background-color: hsl(0, 0%, 96.5%);
  width: 100%;
  border-radius: 4px;
`;

const ChipWrap = styled("div")`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilterChip = styled(Chip)`
  margin: 5px;
  background-color: #e0e0e0;
  max-width: 240px;
`;

SelectedFilters.propTypes = {
  selectedFilterValues: PropTypes.object.isRequired,
  onSetFilters: PropTypes.func.isRequired,
  enableClear: PropTypes.bool,
  clearAllFilters: PropTypes.func,
  onDeleteStore: PropTypes.func,
  onClearStore: PropTypes.func
};

export default SelectedFilters;
